import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PlanVariantEditor, PlanVariantPicker } from '@/components/PlanVariantEditor';
import { usePlanRegistryVersion } from '@/lib/usePlanVariants';
import { buildPlannerParticipations } from '@/lib/plannerParticipations';
import { renderNodeToPng, triggerDownload } from '@/lib/downloadPng';
import { ArrowLeftRight, ArrowRight, Copy, Download, Wrench } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api, type Participation, type SlotKey as EventSlotKey } from '@/lib/api';
import { cn } from '@/lib/utils';
import { subColor } from '@/lib/subColor';
import { buildSlotDmText, DM_TEXT_MAX } from '@/lib/slotDm';
import {
  DS_NON_SILO_SLOTS,
  resolveDSVariant,
  type DSNonSiloSlot,
  type DSPhaseDef,
  type DSPlanConfig,
  type DSSlotKey,
  type Priority,
  type VariantMeta,
} from '@/plans';
import { useCurrentAlliance } from '@/lib/useCurrentAlliance';

const PRIORITY_DOT: Record<Priority, string> = {
  critical: 'bg-red-500',
  high: 'bg-orange-500',
  medium: 'bg-yellow-400',
  lower: 'bg-green-500',
};

function slotCapacity(phase: DSPhaseDef, slot: DSSlotKey) {
  return phase.capacities[slot];
}

function slotRows(phase: DSPhaseDef, slot: DSSlotKey) {
  return Math.max(1, slotCapacity(phase, slot));
}

function displayName(p: Participation) {
  return p.player.displayName || p.player.name;
}

function powerOf(p: Participation) {
  return p.player.latestSnapshot?.m1Power ?? 0;
}

type PhaseAssignments = Partial<Record<DSSlotKey, string[]>>;
type SubEntry = { out: string; in: string };

function computeAutofill(
  participations: Participation[],
  slot: EventSlotKey,
  config: DSPlanConfig,
): { phases: PhaseAssignments[]; subs: SubEntry[] } | null {
  const inSlot = participations.filter((p) => p.assignedSlot === slot);
  // Flex-marked players are always treated as strongest: they stage on the
  // Silo (in DS) / VL (in CS) regardless of raw M1, so they must sort ahead
  // of every non-flex player in the strength order that drives homeByRank
  // and strengthClaims.
  const byStrength = (a: Participation, b: Participation) => {
    if (!!a.flex !== !!b.flex) return a.flex ? -1 : 1;
    return powerOf(b) - powerOf(a);
  };
  const starters = inSlot
    .filter((p) => p.assignedRole === 'starter')
    .sort(byStrength);
  const subList = inSlot
    .filter((p) => p.assignedRole === 'sub')
    .sort(byStrength);

  if (starters.length === 0 && subList.length === 0) return null;

  const flexStarters = starters.filter((p) => p.flex);
  const nonFlexStarters = starters.filter((p) => !p.flex);
  const flexNames = flexStarters.map(displayName);

  // Home layout by rank. Rank 0 = strongest non-flex (already sorted M1 desc).
  const home: Record<DSNonSiloSlot, Participation[]> = {
    infoCenter: [],
    oilRefinery1: [],
    oilRefinery2: [],
    fieldHospital1: [],
    fieldHospital2: [],
    fieldHospital3: [],
    fieldHospital4: [],
    scienceHub: [],
    arsenal: [],
    mercenary: [],
  };
  const homeConsumed = Math.min(nonFlexStarters.length, config.homeByRank.length);
  for (let i = 0; i < homeConsumed; i++) {
    home[config.homeByRank[i]].push(nonFlexStarters[i]);
  }
  const leftover = nonFlexStarters.slice(homeConsumed);

  const coreIds = new Set(
    config.coreRanks.map((r) => nonFlexStarters[r]?.playerId).filter(Boolean),
  );

  const byM1Desc = byStrength;

  const phases: PhaseAssignments[] = config.phases.map((phase) => {
    const claimedBySlot: Partial<Record<DSSlotKey, Participation[]>> = {};
    const claimedIds = new Set<number>();
    if (phase.strengthClaims) {
      let cursor = 0;
      for (const claim of phase.strengthClaims) {
        const take = starters.slice(cursor, cursor + claim.count);
        cursor += claim.count;
        claimedBySlot[claim.slot] = take;
        for (const p of take) claimedIds.add(p.playerId);
      }
    }

    const kept: Record<DSNonSiloSlot, Participation[]> = {} as Record<
      DSNonSiloSlot,
      Participation[]
    >;
    const displaced: Participation[] = [];

    for (const key of DS_NON_SILO_SLOTS) {
      if (claimedBySlot[key]) {
        kept[key] = claimedBySlot[key]!.slice(0, phase.capacities[key]);
        continue;
      }
      const cap = phase.capacities[key];
      const list = home[key].filter((p) => !claimedIds.has(p.playerId));
      const keep = list.slice(0, Math.min(cap, list.length));
      displaced.push(...list.slice(keep.length));
      kept[key] = keep;
    }

    const unclaimedFlex = phase.strengthClaims
      ? flexStarters.filter((p) => !claimedIds.has(p.playerId))
      : [];

    const filteredLeftover = leftover.filter((p) => !claimedIds.has(p.playerId));
    const pool = [...displaced, ...filteredLeftover, ...unclaimedFlex].sort(byM1Desc);
    let pi = 0;

    for (const key of DS_NON_SILO_SLOTS) {
      if (claimedBySlot[key]) continue;
      const cap = phase.capacities[key];
      while (kept[key].length < cap && pi < pool.length) {
        kept[key].push(pool[pi]);
        pi += 1;
      }
    }

    const siloCap = phase.capacities.nuclearSilo;
    let siloPlayers: Participation[];
    if (claimedBySlot.nuclearSilo) {
      siloPlayers = claimedBySlot.nuclearSilo.slice(0, siloCap);
    } else {
      // Default: flex baseline + strongest displaced (P1 holding position).
      siloPlayers = [...flexStarters];
      const extrasNeeded = Math.max(0, siloCap - flexStarters.length);
      let extrasAdded = 0;
      while (extrasAdded < extrasNeeded && pi < pool.length) {
        siloPlayers.push(pool[pi]);
        pi += 1;
        extrasAdded += 1;
      }
    }

    const assign: PhaseAssignments = {};
    for (const key of DS_NON_SILO_SLOTS) {
      const cap = phase.capacities[key];
      const sorted = [...kept[key]].sort(byM1Desc).map(displayName);
      while (sorted.length < cap) sorted.push('');
      assign[key] = sorted;
    }
    const siloNames = [...siloPlayers].sort(byM1Desc).map(displayName);
    while (siloNames.length < siloCap) siloNames.push('');
    assign.nuclearSilo = siloNames.slice(0, siloCap);

    return assign;
  });

  // Sub-eligible pool: exclude flex, silo core, neverSub.
  const subEligible = nonFlexStarters.filter(
    (p) => !coreIds.has(p.playerId) && !p.player.neverSub,
  );
  // Pair exactly as many starters as we have subs — if only 8 subs signed up,
  // the 8 weakest sub-eligible starters get replaced (mirrors slotDm.ts).
  const subCount = subList.length;
  const weakest = [...subEligible]
    .sort((a, b) => powerOf(a) - powerOf(b))
    .slice(0, subCount);
  const weakestByM1Desc = weakest.sort(byM1Desc);
  const subs: SubEntry[] = Array.from({ length: subCount }, (_, i) => ({
    out: weakestByM1Desc[i] ? displayName(weakestByM1Desc[i]) : '',
    in: subList[i] ? displayName(subList[i]) : '',
  }));

  // silence lint: flexNames unused when claim always covers flex (P2 case).
  void flexNames;

  return { phases, subs };
}

function PlanCopyDmButton({
  eventSlot,
  slotStart,
  participations,
}: {
  eventSlot: EventSlotKey;
  slotStart: string | null;
  participations: Participation[];
}) {
  const [copied, setCopied] = useState(false);
  const text = useMemo(() => {
    const inSlot = participations.filter((p) => p.assignedSlot === eventSlot);
    const starters = inSlot.filter((p) => p.assignedRole === 'starter');
    const subs = inSlot.filter((p) => p.assignedRole === 'sub');
    return buildSlotDmText({
      eventType: 'desert_storm',
      slot: eventSlot,
      startsAtServer: slotStart ?? undefined,
      starters,
      subs,
    });
  }, [participations, eventSlot, slotStart]);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };
  return (
    <button
      onClick={onCopy}
      className="inline-flex items-center gap-1 rounded border border-emerald-600 bg-emerald-900/40 px-2 py-1 text-xs font-medium text-emerald-100 hover:bg-emerald-800/60"
      title={`Copy DM text (${text.length}/${DM_TEXT_MAX})\n\n${text}`}
    >
      <Copy size={12} /> {copied ? 'Copied' : 'Copy DM'}
    </button>
  );
}

export default function DSPlan() {
  const [params, setParams] = useSearchParams();
  const eventIdStr = params.get('eventId');
  const eventSlotParam = params.get('slot');
  const variantParam = params.get('variant');
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me().catch(() => null) });
  const registryVersion = usePlanRegistryVersion();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorInitialKey, setEditorInitialKey] = useState<string | null>(null);
  const eventId = eventIdStr ? Number(eventIdStr) : null;
  const eventSlot: EventSlotKey | null =
    eventSlotParam === 'A' || eventSlotParam === 'B' ? eventSlotParam : null;
  const config = useMemo(
    () => resolveDSVariant(variantParam),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [variantParam, registryVersion],
  );

  const eventQuery = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => api.getEvent(eventId!),
    enabled: eventId !== null,
  });
  const participationsQuery = useQuery({
    queryKey: ['participations', eventId],
    queryFn: () => api.listParticipations(eventId!),
    enabled: eventId !== null,
  });

  const boardRef = useRef<HTMLDivElement>(null);
  const [assignments, setAssignments] = useState<PhaseAssignments[]>(() =>
    config.phases.map(() => ({})),
  );
  const [subs, setSubs] = useState<SubEntry[]>([]);
  const [downloading, setDownloading] = useState(false);
  const filledForRef = useRef<string | null>(null);
  const [plannerMode, setPlannerMode] = useState(false);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    if (plannerMode) return;
    if (eventId === null || !eventSlot) return;
    if (!participationsQuery.data) return;
    const key = `${eventId}|${eventSlot}`;
    if (filledForRef.current === key) return;
    const filled = computeAutofill(participationsQuery.data, eventSlot, config);
    if (!filled) return;
    setAssignments(filled.phases);
    setSubs(filled.subs);
    filledForRef.current = key;
  }, [eventId, eventSlot, participationsQuery.data, config, plannerMode]);

  // Planner mode: re-fill with synthetic Player-1..N whenever the mode is
  // toggled on or the config changes.
  useEffect(() => {
    if (!plannerMode) return;
    const starters = (config.homeByRank?.length ?? 18) + 2; // + 2 flex
    const parts = buildPlannerParticipations({ starters, subs: 10, flex: 2 });
    const filled = computeAutofill(parts, 'A', config);
    if (!filled) return;
    setAssignments(filled.phases);
    setSubs(filled.subs);
  }, [plannerMode, config]);

  const setSlot = (phaseIdx: number, slot: DSSlotKey, lineIdx: number, value: string) => {
    setAssignments((prev) => {
      const next = prev.map((p) => ({ ...p }));
      const rows = slotRows(config.phases[phaseIdx], slot);
      const cur = next[phaseIdx][slot] ?? Array.from({ length: rows }, () => '');
      const arr = [...cur];
      while (arr.length < rows) arr.push('');
      arr[lineIdx] = value;
      next[phaseIdx][slot] = arr;
      return next;
    });
  };

  const download = async () => {
    if (!boardRef.current) return;
    setDownloading(true);
    try {
      const dataUrl = await renderNodeToPng(boardRef.current, {
        pixelRatio: 4,
        backgroundColor: '#0b1220',
      });
      const suffix =
        eventQuery.data && eventSlot
          ? `${eventQuery.data.eventDate}-slot${eventSlot}`
          : new Date().toISOString().slice(0, 10);
      triggerDownload(dataUrl, `ds-plan-${suffix}.png`);
    } finally {
      setDownloading(false);
    }
  };

  const slotStart = useMemo(() => {
    if (!eventQuery.data || !eventSlot) return null;
    return eventQuery.data.slots.find((s) => s.slotKey === eventSlot)?.startsAtServer ?? null;
  }, [eventQuery.data, eventSlot]);

  const contextLabel =
    eventQuery.data && eventSlot
      ? `${eventQuery.data.eventDate} · Slot ${eventSlot}${slotStart ? ` @ ${slotStart}` : ''}`
      : 'Blank template';

  const subMap = useMemo(() => {
    const m = new Map<string, number>();
    subs.forEach((s, i) => {
      const key = s.out.trim();
      if (key) m.set(key, i + 1);
    });
    return m;
  }, [subs]);

  const movedByPhase = useMemo(() => {
    const result: Set<string>[] = [];
    for (let i = 0; i < assignments.length; i++) {
      const set = new Set<string>();
      if (i > 0) {
        const prevSlotOf = new Map<string, DSSlotKey>();
        for (const [key, list] of Object.entries(assignments[i - 1]) as [
          DSSlotKey,
          string[],
        ][]) {
          for (const name of list) {
            const trimmed = name.trim();
            if (trimmed) prevSlotOf.set(trimmed, key);
          }
        }
        for (const [key, list] of Object.entries(assignments[i]) as [
          DSSlotKey,
          string[],
        ][]) {
          for (const name of list) {
            const trimmed = name.trim();
            if (!trimmed) continue;
            const prevSlot = prevSlotOf.get(trimmed);
            if (prevSlot && prevSlot !== key) set.add(trimmed);
          }
        }
      }
      result.push(set);
    }
    return result;
  }, [assignments]);

  const lastPhaseIdx = config.phases.length - 1;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold">Desert Storm — Battle Plan</h1>
          <p className="text-xs text-slate-500">{contextLabel}</p>
          <div className="mt-2 rounded border border-orange-500/60 bg-orange-950/30 px-2 py-1 inline-flex">
            <PlanVariantPicker
              kind="ds"
              currentKey={variantParam}
              user={meQuery.data ?? null}
              onChange={(key) => {
                const p = new URLSearchParams(params);
                p.set('variant', key);
                setParams(p, { replace: true });
              }}
              onEdit={(key) => {
                setEditorInitialKey(key);
                setEditorOpen(true);
              }}
              showAdminActions={editMode}
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {eventId !== null && eventSlot && (
            <PlanCopyDmButton
              eventSlot={eventSlot}
              slotStart={slotStart}
              participations={participationsQuery.data ?? []}
            />
          )}
          {eventId !== null && eventSlot && (
            <Link
              to={`/plans/ds?eventId=${eventId}&slot=${eventSlot === 'A' ? 'B' : 'A'}&variant=${variantParam ?? 'desertstorm'}`}
              className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-900/40 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800"
              title={`Switch to Slot ${eventSlot === 'A' ? 'B' : 'A'}`}
            >
              <ArrowLeftRight size={12} /> Zu Slot {eventSlot === 'A' ? 'B' : 'A'}
            </Link>
          )}
          <button
            onClick={download}
            disabled={downloading}
            className="inline-flex items-center gap-1 rounded border border-indigo-500 bg-indigo-600/40 px-3 py-1 text-sm font-medium text-indigo-100 hover:bg-indigo-600/60 disabled:opacity-50"
          >
            <Download size={14} />
            {downloading ? 'Rendering…' : 'Download PNG'}
          </button>
          <button
            onClick={() => setEditMode((v) => !v)}
            className={cn(
              'inline-flex items-center gap-1 rounded border px-2 py-1 text-xs',
              editMode
                ? 'border-amber-500/60 bg-amber-600/20 text-amber-100'
                : 'border-slate-800 bg-transparent text-slate-500 hover:bg-slate-800 hover:text-slate-300',
            )}
            title="Show admin controls: planner mode, save / edit variant, set default"
          >
            <Wrench size={12} /> Edit mode
          </button>
        </div>
      </div>

      {editMode && (
        <div className="flex flex-wrap items-center gap-2 rounded border border-amber-800/50 bg-amber-950/20 px-3 py-2 text-xs">
          <span className="uppercase tracking-wider text-amber-300/80">Admin</span>
          <button
            onClick={() => setPlannerMode((v) => !v)}
            className={cn(
              'rounded border px-2 py-1',
              plannerMode
                ? 'border-orange-500 bg-orange-600 text-white'
                : 'border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700',
            )}
            title="Fill tiles with Player 1..N (strongest first) to plan without real names"
          >
            {plannerMode ? 'Planner ✓' : 'Planner'}
          </button>
          <span className="text-slate-600">·</span>
          <span className="text-slate-400">
            Variant admin actions shown inside the picker above.
          </span>
        </div>
      )}

      <VariantInfo meta={config.meta} />
      {editorOpen && meQuery.data && (
        <PlanVariantEditor
          open
          onClose={() => setEditorOpen(false)}
          kind="ds"
          user={meQuery.data}
          initialVariantKey={editorInitialKey}
          onSaved={(key) => {
            const p = new URLSearchParams(params);
            p.set('variant', key);
            setParams(p, { replace: true });
          }}
        />
      )}

      <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
      <div
        ref={boardRef}
        className={cn(
          'rounded-lg border border-orange-500/40 bg-[#0b1220] px-4 pb-4 text-slate-100',
          downloading ? 'pt-0' : 'pt-4',
        )}
        style={{ width: config.phases.length === 1 ? 1230 : 1536 }}
      >
        <div className="mb-3 border-b border-slate-800 pb-2 text-center">
          <div className="text-xl font-bold tracking-wide">
            Desert Storm
            {eventQuery.data && eventSlot && (
              <>
                {' — '}
                <span className="text-orange-400">{eventQuery.data.eventDate}</span>
                {' · '}
                <span>Slot {eventSlot}</span>
                {slotStart && (
                  <>
                    {' @ '}
                    <span>{slotStart}</span>
                  </>
                )}
              </>
            )}
          </div>
          <div className="mt-1 text-[10px] uppercase tracking-widest text-slate-400">
            Ruleset:{' '}
            <span className="font-semibold text-orange-400">{config.meta.name}</span>
            {config.meta.draft && (
              <span className="ml-2 rounded bg-amber-600/40 px-1 text-amber-100">DRAFT</span>
            )}
            <span className="mx-2 text-slate-600">·</span>
            {config.meta.integratedAt
              ? `Integrated ${config.meta.integratedAt}`
              : 'Not yet integrated'}
          </div>
        </div>

        <div
          className={cn(
            'grid gap-3',
            config.phases.length === 1 ? 'grid-cols-1' : 'grid-cols-2',
          )}
        >
          {config.phases.map((phase, i) => (
            <PhaseColumn
              key={i}
              phase={phase}
              slotNotes={config.slotNotes}
              flexSlots={config.flexSlots}
              subMap={i === lastPhaseIdx ? subMap : undefined}
              movedNames={movedByPhase[i]}
              values={assignments[i]}
              onChange={(slot, lineIdx, value) => setSlot(i, slot, lineIdx, value)}
            />
          ))}
        </div>

        <div className="mt-4 grid grid-cols-[2.6fr_1fr_0.5fr] gap-3">
          <SubstitutionList subs={subs} setSubs={setSubs} />
          <ImportantNotes />
          <FoaBrand />
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-center gap-x-6 gap-y-1 rounded border border-orange-500/40 bg-slate-950/60 px-4 py-2 text-[11px] tracking-wider text-slate-300">
          <span className="flex items-center gap-1.5">
            <span className="flex items-center rounded bg-red-600 px-0.5 text-white">
              <ArrowRight size={10} strokeWidth={3} />
            </span>
            = MOVED FROM ANOTHER BUILDING THIS PHASE
          </span>
          <span className="flex items-center gap-1.5">
            <span className="flex items-center gap-0.5">
              {[1, 2, 3].map((n) => {
                const c = subColor(n);
                return (
                  <span
                    key={n}
                    className={cn('flex items-center gap-0.5 rounded px-1 text-[9px] font-bold', c.bg, c.text)}
                  >
                    <ArrowLeftRight size={9} strokeWidth={3} />
                    {n}
                  </span>
                );
              })}
            </span>
            = SUB TAKES OVER — COLOR MATCHES THE LIST BELOW
          </span>
          <span className="tracking-widest">DM TO TRADE SPOTS 📦</span>
        </div>
      </div>
      </div>
    </div>
  );
}

function PhaseColumn({
  phase,
  slotNotes,
  flexSlots,
  values,
  onChange,
  subMap,
  movedNames,
}: {
  phase: DSPhaseDef;
  slotNotes?: Partial<Record<DSSlotKey, string>>;
  flexSlots?: DSSlotKey[];
  values: PhaseAssignments;
  onChange: (slot: DSSlotKey, lineIdx: number, value: string) => void;
  subMap?: Map<string, number>;
  movedNames?: Set<string>;
}) {
  const flexSet = useMemo(() => new Set(flexSlots ?? []), [flexSlots]);

  const slot = (
    key: DSSlotKey,
    label: string,
    opts?: { className?: string; flex?: boolean; note?: string },
  ) => {
    const priority = phase.priorities[key];
    const cap = slotCapacity(phase, key);
    const abandoned = key !== 'nuclearSilo' && cap === 0 && priority !== null;
    const isFlex = opts?.flex || flexSet.has(key);
    return (
      <BuildingSlot
        label={label}
        priority={priority}
        rows={slotRows(phase, key)}
        className={opts?.className}
        values={values[key] ?? []}
        onChange={(lineIdx, value) => onChange(key, lineIdx, value)}
        subMap={subMap}
        movedNames={movedNames}
        abandoned={abandoned}
        flex={isFlex}
        note={opts?.note ?? slotNotes?.[key]}
        emphasizeNote={key.startsWith('fieldHospital')}
      />
    );
  };

  // Silo shows as "Flex" while Arsenal/Mercenary are still locked (pre-collapse
  // in the two-phase variant). In the single-map variant it stays "Nuclear Silo"
  // but is still rendered with flex styling via config.flexSlots.
  const siloFlex = phase.priorities.arsenal === null;
  const siloLabel = siloFlex ? 'Flex' : 'Nuclear Silo';
  const siloFlexStyle = siloFlex || flexSet.has('nuclearSilo');

  return (
    <div className="flex h-full flex-col rounded-md border border-orange-500/40 bg-slate-950/60 p-3">
      <div className="mb-2 text-center">
        <div className="text-lg font-bold tracking-wide">
          {phase.title} — <span className={phase.timeColor}>{phase.time}</span>
        </div>
        {phase.subtitle && (
          <div className={cn('text-xs font-semibold tracking-wider', phase.subtitleColor)}>
            {phase.subtitle}
          </div>
        )}
      </div>

      {/* 8-col layout so gaps can be half a building tile. Each building
          tile spans 2 cols; a "half-column" gap = 1 col. Rows 1 & 4 have a
          half-gap at each edge; rows 2 & 3 have half-gaps around Silo.
          Fixed row heights so P1 and P2 render the map at identical size
          regardless of per-phase capacity differences. */}
      <div className="grid grid-cols-8 grid-rows-[repeat(4,minmax(0,1fr))] gap-1.5 h-[30rem]">
        {slot('infoCenter', 'Info Center', { className: 'col-start-2 col-span-2 row-start-1' })}
        {slot('arsenal', 'Arsenal', { className: 'col-start-4 col-span-2 row-start-1' })}
        {slot('fieldHospital4', 'Field Hospital IV', { className: 'col-start-6 col-span-2 row-start-1' })}

        {slot('oilRefinery1', 'Oil Refinery I', { className: 'col-start-1 col-span-2 row-start-2' })}
        {slot('fieldHospital1', 'Field Hospital I', { className: 'col-start-1 col-span-2 row-start-3' })}

        {slot('nuclearSilo', siloLabel, {
          className: 'col-start-4 col-span-2 row-start-2 row-span-2',
          flex: siloFlexStyle,
        })}

        {slot('fieldHospital2', 'Field Hospital II', { className: 'col-start-7 col-span-2 row-start-2' })}
        {slot('oilRefinery2', 'Oil Refinery II', { className: 'col-start-7 col-span-2 row-start-3' })}

        {slot('fieldHospital3', 'Field Hospital III', { className: 'col-start-2 col-span-2 row-start-4' })}
        {slot('mercenary', 'Mercenary Factory', { className: 'col-start-4 col-span-2 row-start-4' })}
        {slot('scienceHub', 'Science Hub', { className: 'col-start-6 col-span-2 row-start-4' })}
      </div>

      <div className="mt-auto pt-3 flex justify-around text-[10px] font-semibold tracking-wider text-slate-300">
        <LegendDot color="bg-red-500" label="CRITICAL" />
        <LegendDot color="bg-orange-500" label="HIGH" />
        <LegendDot color="bg-yellow-400" label="MEDIUM" />
        <LegendDot color="bg-green-500" label="LOWER" />
      </div>
      <div className="mt-2 border-t border-slate-800 pt-1 text-center text-[10px] tracking-wider text-slate-400">
        20 STARTERS ASSIGNED <span className="text-orange-400">•</span> FLEX PLAYERS ON FLEX
      </div>
    </div>
  );
}

function BuildingSlot({
  label,
  priority,
  rows,
  className,
  values,
  onChange,
  subMap,
  movedNames,
  abandoned = false,
  flex = false,
  note,
  emphasizeNote = false,
}: {
  label: string;
  priority: Priority | null;
  rows: number;
  className?: string;
  values: string[];
  onChange: (lineIdx: number, value: string) => void;
  subMap?: Map<string, number>;
  movedNames?: Set<string>;
  abandoned?: boolean;
  flex?: boolean;
  note?: string;
  emphasizeNote?: boolean;
}) {
  const closed = priority === null;
  const disabled = closed || abandoned;
  const showNote = disabled && !!note;
  return (
    <div
      className={cn(
        'flex min-h-0 flex-col overflow-hidden',
        className,
        disabled && !showNote && 'opacity-40',
        disabled && showNote && 'opacity-70',
      )}
    >
      <div
        className={cn(
          'rounded-t px-1.5 py-0.5',
          flex ? 'bg-blue-700' : 'bg-slate-900',
        )}
      >
        <div className="flex items-center gap-1">
          <span
            className={cn(
              'h-2 w-2 rounded-full',
              disabled ? 'bg-slate-600' : PRIORITY_DOT[priority],
            )}
          />
          <span
            className={cn(
              'truncate text-[10px] font-semibold',
              flex ? 'text-blue-50' : 'text-slate-200',
            )}
          >
            {label}
            {closed && <span className="ml-1 text-slate-500">— CLOSED</span>}
            {abandoned && !closed && (
              <span className="ml-1 text-slate-500">— ABANDONED 💀</span>
            )}
          </span>
        </div>
        {emphasizeNote && !disabled && note && (
          <div
            className="line-clamp-2 whitespace-normal text-[9px] font-bold uppercase leading-[1.1] tracking-wider text-red-400"
            title={note}
          >
            {note}
          </div>
        )}
      </div>
      <div
        className={cn(
          'flex-1 rounded-b border p-0.5',
          disabled
            ? 'border-slate-800 bg-slate-800/60'
            : flex
              ? 'border-blue-500 bg-blue-50'
              : 'border-slate-700 bg-white/95',
        )}
      >
        {showNote ? (
          <div
            className={cn(
              'flex h-full items-center justify-center px-1 text-center leading-tight',
              emphasizeNote
                ? 'text-[11px] font-bold text-red-400'
                : 'text-[10px] italic text-slate-300',
            )}
          >
            {note}
          </div>
        ) : (
          <>
            {!disabled && note && !emphasizeNote && (
              <div
                className={cn(
                  'mb-0.5 truncate px-1 text-center text-[9px] italic leading-tight',
                  flex ? 'text-blue-800' : 'text-slate-500',
                )}
                title={note}
              >
                {note}
              </div>
            )}
            {Array.from({ length: rows }, (_, i) => {
          const value = disabled ? '' : values[i] ?? '';
          const trimmed = value.trim();
          const subPos = subMap && trimmed ? subMap.get(trimmed) : undefined;
          const moved = !!trimmed && !!movedNames?.has(trimmed);
          return (
            <div
              key={i}
              className={cn(
                'relative flex items-center border-b last:border-b-0',
                disabled
                  ? 'border-slate-800'
                  : flex
                    ? 'border-blue-200'
                    : 'border-slate-200',
              )}
            >
              {moved && (
                <span
                  className="pointer-events-none absolute left-0.5 top-1/2 z-10 flex -translate-y-1/2 items-center rounded bg-red-600 px-0.5 text-white"
                  title="Moved from another building this phase"
                >
                  <ArrowRight size={10} strokeWidth={3} />
                </span>
              )}
              <input
                value={value}
                disabled={disabled}
                onChange={(e) => onChange(i, e.target.value)}
                className={cn(
                  'block w-full bg-transparent px-5 py-0.5 text-center text-[13px] focus:outline-none focus:ring-1 focus:ring-orange-400',
                  disabled ? 'cursor-not-allowed text-slate-500' : 'text-slate-900',
                )}
              />
              {subPos !== undefined && (() => {
                const c = subColor(subPos);
                return (
                  <span
                    className={cn(
                      'pointer-events-none absolute right-0.5 top-1/2 z-10 flex -translate-y-1/2 items-center gap-0.5 rounded px-1 text-[9px] font-bold',
                      c.bg,
                      c.text,
                    )}
                    title={`Sub #${subPos} takes over`}
                  >
                    <ArrowLeftRight size={9} strokeWidth={3} />
                    {subPos}
                  </span>
                );
              })()}
            </div>
          );
            })}
          </>
        )}
      </div>
    </div>
  );
}

function VariantInfo({ meta }: { meta: VariantMeta }) {
  return (
    <details className="group rounded-lg border border-slate-800 bg-slate-900/40 open:pb-3">
      <summary className="flex cursor-pointer items-center justify-between gap-3 px-3 py-2 text-sm">
        <span>
          <span className="text-slate-400">Ruleset in effect:</span>{' '}
          <span className="font-semibold text-orange-400">{meta.name}</span>
          {meta.draft && (
            <span className="ml-2 rounded bg-amber-600/40 px-1 text-xs text-amber-100">
              DRAFT
            </span>
          )}
          <span className="ml-2 text-xs text-slate-500">
            {meta.integratedAt ? `· integrated ${meta.integratedAt}` : '· not yet integrated'}
          </span>
        </span>
        <span className="text-xs text-slate-500 group-open:hidden">Show details ▾</span>
        <span className="hidden text-xs text-slate-500 group-open:inline">Hide ▴</span>
      </summary>
      <div className="space-y-2 px-3 text-xs text-slate-300">
        <p>{meta.shortDescription}</p>
        <ul className="list-disc space-y-1 pl-5">
          {meta.highlights.map((h, i) => (
            <li key={i}>{h}</li>
          ))}
        </ul>
        <p className="text-[11px] text-slate-500">
          Reference: <code className="text-slate-400">{meta.rulebookRef}</code>
        </p>
      </div>
    </details>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-1">
      <span className={cn('h-2 w-2 rounded-full', color)} />
      <span>{label}</span>
    </div>
  );
}

function SubstitutionList({
  subs,
  setSubs,
}: {
  subs: SubEntry[];
  setSubs: React.Dispatch<React.SetStateAction<SubEntry[]>>;
}) {
  const setEntry = (idx: number, field: 'out' | 'in', value: string) => {
    setSubs((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };
  // Split into two columns; left column takes the ceil so 1 sub renders as
  // a single left-hand row rather than an empty pair.
  const half = Math.max(1, Math.ceil(subs.length / 2));
  return (
    <div className="rounded-md border border-orange-500/40 bg-slate-950/60 p-3">
      <div className="text-center text-sm font-bold tracking-widest text-orange-400">
        SUBSTITUTION LIST
      </div>
      <div className="text-center text-[10px] tracking-wider text-slate-400">
        (IN ORDER OF POWER)
      </div>
      <table className="mt-2 w-full table-fixed text-[11px]">
        <colgroup>
          <col className="w-7" />
          <col />
          <col className="w-4" />
          <col />
          <col className="w-3" />
          <col className="w-7" />
          <col />
          <col className="w-4" />
          <col />
        </colgroup>
        <thead>
          <tr className="text-[10px] font-semibold tracking-wider text-slate-400">
            <th className="text-left font-semibold">#</th>
            <th className="text-left font-semibold">STARTER (OUT)</th>
            <th />
            <th className="text-left font-semibold">SUB (IN)</th>
            <th />
            <th className="text-left font-semibold">#</th>
            <th className="text-left font-semibold">STARTER (OUT)</th>
            <th />
            <th className="text-left font-semibold">SUB (IN)</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: half }).map((_, row) => {
            const leftIdx = row;
            const rightIdx = row + half;
            const left = subs[leftIdx];
            const right = subs[rightIdx];
            const leftC = subColor(leftIdx + 1);
            const rightC = right ? subColor(rightIdx + 1) : null;
            return (
              <tr key={row} className="align-middle">
                <td className="py-0.5">
                  {left && (
                    <span className={cn('inline-flex h-4 w-5 items-center justify-center rounded text-[10px] font-bold', leftC.bg, leftC.text)}>
                      {leftIdx + 1}
                    </span>
                  )}
                </td>
                <td className="py-0.5">
                  {left && (
                    <input
                      value={left.out}
                      onChange={(e) => setEntry(leftIdx, 'out', e.target.value)}
                      className="w-full min-w-0 border-0 bg-transparent px-1 py-0.5 text-slate-100 focus:outline-none focus:ring-1 focus:ring-orange-400/60 rounded"
                    />
                  )}
                </td>
                <td className="py-0.5 text-center text-slate-500">{left ? '→' : ''}</td>
                <td className="py-0.5">
                  {left && (
                    <input
                      value={left.in}
                      onChange={(e) => setEntry(leftIdx, 'in', e.target.value)}
                      className="w-full min-w-0 border-0 bg-transparent px-1 py-0.5 text-slate-100 focus:outline-none focus:ring-1 focus:ring-orange-400/60 rounded"
                    />
                  )}
                </td>
                <td className="border-l border-slate-800" />
                <td className="py-0.5 pl-2">
                  {right && rightC && (
                    <span className={cn('inline-flex h-4 w-5 items-center justify-center rounded text-[10px] font-bold', rightC.bg, rightC.text)}>
                      {rightIdx + 1}
                    </span>
                  )}
                </td>
                <td className="py-0.5">
                  {right && (
                    <input
                      value={right.out}
                      onChange={(e) => setEntry(rightIdx, 'out', e.target.value)}
                      className="w-full min-w-0 border-0 bg-transparent px-1 py-0.5 text-slate-100 focus:outline-none focus:ring-1 focus:ring-orange-400/60 rounded"
                    />
                  )}
                </td>
                <td className="py-0.5 text-center text-slate-500">{right ? '→' : ''}</td>
                <td className="py-0.5">
                  {right && (
                    <input
                      value={right.in}
                      onChange={(e) => setEntry(rightIdx, 'in', e.target.value)}
                      className="w-full min-w-0 border-0 bg-transparent px-1 py-0.5 text-slate-100 focus:outline-none focus:ring-1 focus:ring-orange-400/60 rounded"
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="mt-3 space-y-1 border-t border-slate-800 pt-2 text-[10px] tracking-wider text-slate-400">
        <div>
          {subs.length === 1
            ? 'THIS PLAYER REPLACES THE ASSIGNED STARTER IF THEY CANNOT CONTINUE.'
            : `THESE ${subs.length} PLAYERS REPLACE THE ASSIGNED STARTER IF THEY CANNOT CONTINUE. MATCH FOR MATCH.`}
        </div>
        <div>ONCE YOUR PERSONAL SCORE IS REACHED AND THE GAME ALLOWS IT, SWAP IN THE SUB AT AROUND 15 MIN REMAINING.</div>
      </div>
    </div>
  );
}

function ImportantNotes() {
  const notes = [
    'All 20 starters are in from the beginning.',
    'Subs please DM to trade spots.',
    'Focus fire on critical targets.',
    'Only Field Hospitals II & IV are staffed. FH I & III are fallbacks if closer to spawn.',
    'Nuclear Silo, Oil Refineries and Info Center are the most important buildings.',
    'Try to hold the middle two — but do not sacrifice Oil or Info for them.',
    'The 4 strongest players are Flex until Phase 2.',
  ];
  return (
    <div className="rounded-md border border-orange-500/40 bg-slate-950/60 p-3">
      <div className="text-center text-sm font-bold tracking-widest text-orange-400">
        IMPORTANT NOTES
      </div>
      <ul className="mt-2 space-y-1.5">
        {notes.map((n, i) => (
          <li key={i} className="flex items-start gap-1.5 text-[11px] leading-tight text-slate-200">
            <span className="mt-0.5 text-orange-400">›</span>
            <span>{n}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FoaBrand() {
  const alliance = useCurrentAlliance();
  const label = alliance ? alliance.tag || alliance.name : '';
  return (
    <div className="flex flex-col items-center justify-center rounded-md border border-orange-500/40 bg-slate-950/60 p-3 text-center">
      {alliance?.logoUrl && <img src={alliance.logoUrl} alt="" className="mb-2 h-16 w-auto rounded" />}
      <div className="text-4xl font-black tracking-tight text-orange-400">{label}</div>
      {alliance?.motto ? (
        <div className="mt-2 font-serif text-sm italic text-orange-300">
          {/* One sentence per line, like the original FoA poster. */}
          {alliance.motto.split(/(?<=[.!?])\s+/).map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
      ) : (
        alliance?.tag && (
          <div className="mt-2 font-serif text-sm italic text-orange-300">{alliance.name}</div>
        )
      )}
      <div className="mt-2 font-serif italic text-white">
        {label} <span className="text-blue-500">♥</span>
      </div>
    </div>
  );
}
